import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { resolveImageUrl } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UploadCloud, X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const MultiImageUploadField = ({ images, onChange, testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/admin/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([...images, data.url]);
      toast.success("Image added.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const addUrl = () => {
    if (!urlInput.trim()) return;
    onChange([...images, urlInput.trim()]);
    setUrlInput("");
  };

  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const next = [...images];
    const target = i + dir;
    if (target < 0 || target >= next.length) return;
    [next[i], next[target]] = [next[target], next[i]];
    onChange(next);
  };

  return (
    <div data-testid={testId} className="space-y-3">
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {images.map((img, i) => (
            <div key={i} data-testid={`${testId}-item-${i}`} className="relative border border-border group">
              <img src={resolveImageUrl(img)} alt="" className="w-full h-20 object-cover" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                <button type="button" data-testid={`${testId}-move-left-${i}`} onClick={() => move(i, -1)} className="text-white p-1"><ChevronLeft className="h-4 w-4" /></button>
                <button type="button" data-testid={`${testId}-remove-${i}`} onClick={() => remove(i)} className="text-white p-1"><X className="h-4 w-4" /></button>
                <button type="button" data-testid={`${testId}-move-right-${i}`} onClick={() => move(i, 1)} className="text-white p-1"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input placeholder="Paste image URL" className="rounded-none flex-1" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} data-testid={`${testId}-url-input`} />
        <Button type="button" variant="outline" className="rounded-none" onClick={addUrl} data-testid={`${testId}-add-url-button`}>Add</Button>
        <Button type="button" variant="outline" size="icon" disabled={uploading} className="rounded-none shrink-0" onClick={() => inputRef.current?.click()} data-testid={`${testId}-upload-button`}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
        </Button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testId}-file-input`} />
      </div>
    </div>
  );
};
